import type { Bbox } from '../geo/geometria';

/**
 * Cliente de la API de Cernicalo Online (api/): areas LiDAR bajo demanda. En desarrollo Vite
 * redirige /api y /almacen al backend (vite.config.ts); en produccion lo hace Caddy.
 */
export const URL_API = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '/api';

export interface HuellaPeticion {
  rc: string;
  geom: string;
  /** Altura conocida (partes del Catastro: plantasSobre x 3 m). */
  alturaM?: number;
  /** 'edificio' = huella de edificio (altura desconocida); 'parte' = parte de edificio (Catastro 3D). */
  tipo?: 'edificio' | 'parte';
}

/** Campos del modelo recortado (contrato_modelo): sin ellos el area es la de siempre (hojas COPC y alturas). */
export interface OpcionesModelo {
  /** true: el backend ejecuta cubiertas, muros, vegetacion, MDT 50 cm y curvas. */
  modelo?: boolean;
  /** GeoJSON Polygon/MultiPolygon en el huso: la parcela o el rectangulo de la zona. */
  poligono?: string;
  /** 0..150 (por defecto 50 en el servidor; la web manda 150 en parcelas y 50 en zonas): hasta donde se calcula alrededor del poligono. */
  margenM?: number;
  /** Equidistancia de las curvas en m (0,5..25; por defecto 1). */
  intervaloCurvas?: number;
}

export interface HojaEstado {
  nombre: string;
  estado: string;
  bytes: number;
  url: string | null;
  error: string | null;
}

export interface AlturaEdificio {
  rc: string;
  sueloZ: number | null;
  aleroM: number | null;
  cumbreraM: number | null;
  dx: number | null;
  dy: number | null;
  desviacionM: number | null;
  huellaCorregida: string | null;
  puntosCubierta: number;
  nota: string | null;
}

export interface EstadoArea {
  id: string;
  // Estados conocidos; el backend puede anadir otros (la interfaz muestra el mensaje tal cual).
  estado: 'en_cola' | 'buscando' | 'descargando' | 'convirtiendo' | 'analizando' | 'modelando' | 'listo' | 'error' | (string & {});
  mensaje: string | null;
  error: string | null;
  bbox: number[];
  epsg: number;
  rc: string | null;
  hojasTotal: number;
  hojasHechas: number;
  hojas: HojaEstado[];
  productos: Record<string, string>;
  alturas: AlturaEdificio[];
  creado: string;
  actualizado: string;
  /** Solo en las areas de modelo: margen con el que se calculo (m). */
  modelo?: boolean;
  margenM?: number;
}

export interface Salud {
  ok: boolean;
  version: string;
  almacen: string;
  pdal: string;
  maxHojas: number;
  maxAreaKm2: number;
  /** Margen maximo que admite el servidor alrededor del poligono del modelo (m). */
  maxMargenM?: number;
}

export class ErrorApi extends Error {
  constructor(mensaje: string, public readonly estado: number) {
    super(mensaje);
    this.name = 'ErrorApi';
  }
}

async function leer<T>(resp: Response): Promise<T> {
  if (resp.status === 429) throw new ErrorApi('Has lanzado demasiadas áreas en la última hora; espera un poco.', 429);
  const texto = await resp.text();
  let datos: unknown = null;
  try { datos = texto ? JSON.parse(texto) : null; } catch { /* no es JSON */ }
  if (!resp.ok) {
    const err = (datos as { error?: string } | null)?.error ?? texto ?? resp.statusText;
    throw new ErrorApi(err || `HTTP ${resp.status}`, resp.status);
  }
  return datos as T;
}

export async function salud(f: typeof fetch = fetch): Promise<Salud> {
  return leer<Salud>(await f(`${URL_API}/salud`));
}

/**
 * Crea un area LiDAR. `opciones` (modelo, poligono, margenM, intervaloCurvas) es opcional: la
 * llamada antigua `crearArea(bbox, epsg, rc, huellas[, fetch])` sigue funcionando.
 */
export async function crearArea(bbox: Bbox, epsg: number, rc: string | null, huellas: HuellaPeticion[], opciones?: OpcionesModelo | typeof fetch, f: typeof fetch = fetch): Promise<EstadoArea> {
  if (typeof opciones === 'function') { f = opciones; opciones = undefined; }
  const resp = await f(`${URL_API}/areas`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bbox, epsg, rc, huellas, ...(opciones ?? {}) }),
  });
  return leer<EstadoArea>(resp);
}

export async function estadoArea(id: string, f: typeof fetch = fetch): Promise<EstadoArea> {
  return leer<EstadoArea>(await f(`${URL_API}/areas/${encodeURIComponent(id)}`));
}

/** Fallos de red seguidos que aguanta la espera de un area (unos 2 minutos). */
const REINTENTOS_ESPERA = 10;

/**
 * Sondea el estado cada `intervaloMs` hasta que el area este lista o falle. Los fallos de red sueltos
 * (en el movil, al bloquear la pantalla, cambiar de aplicacion o de red) se reintentan: antes uno solo
 * cortaba una espera de minutos con «Modelo LiDAR: Load failed».
 */
export async function esperarArea(id: string, onEstado: (e: EstadoArea) => void, senal?: AbortSignal, intervaloMs = 2500, f: typeof fetch = fetch): Promise<EstadoArea> {
  let fallos = 0;
  for (;;) {
    if (senal?.aborted) throw new Error('Cancelado');
    let e: EstadoArea;
    try {
      e = await estadoArea(id, f);
      fallos = 0;
    } catch (err) {
      const definitivo = err instanceof ErrorApi && err.estado >= 400 && err.estado < 500 && err.estado !== 429;
      if (definitivo || ++fallos > REINTENTOS_ESPERA) throw err;
      await new Promise((r) => setTimeout(r, Math.min(15_000, intervaloMs * fallos)));
      continue;
    }
    onEstado(e);
    if (e.estado === 'listo' || e.estado === 'error') return e;
    await new Promise((r) => setTimeout(r, intervaloMs));
  }
}

export interface PeticionDxf3d {
  epsg: number;
  origen: number[] | null;
  terreno: { x0: number; y0: number; paso: number; columnas: number; filas: number; z: (number | null)[] } | null;
  edificios: { rc: string; geom: string; zBase: number; zTecho: number; zAlero: number | null }[];
  curvas: { nivel: number; puntos: number[][]; cerrada: boolean }[] | null;
  rcDestacada: string | null;
}

/** DXF 3D generado en el backend con netDxf (fase 4). Devuelve los bytes del fichero. */
export async function dxf3d(p: PeticionDxf3d, f: typeof fetch = fetch): Promise<Blob> {
  const resp = await f(`${URL_API}/dxf3d`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(p) });
  if (!resp.ok) {
    let err = `HTTP ${resp.status}`;
    try { err = ((await resp.json()) as { error?: string }).error ?? err; } catch { /* sin cuerpo */ }
    throw new ErrorApi(err, resp.status);
  }
  return resp.blob();
}

// ---------- Busqueda nacional (fase 5): base DuckDB del servidor generada por lotes/ ----------

export interface FilaNacional {
  rc: string;
  etiqueta: string | null;
  areaM2: number | null;
  cx: number;
  cy: number;
  uso: string | null;
  anio: number | null;
  plantas: number | null;
  viviendas: number | null;
  supConstruidaM2: number | null;
  huellaM2: number | null;
  municipio: string | null;
}

export interface CoberturaNacional {
  disponible: boolean;
  cobertura: { municipio: string; parcelas: number }[] | null;
}

let coberturaCache: Promise<CoberturaNacional> | null = null;

export function coberturaNacional(f: typeof fetch = fetch): Promise<CoberturaNacional> {
  if (!coberturaCache) {
    coberturaCache = f(`${URL_API}/nacional`).then((r) => (r.ok ? r.json() : { disponible: false, cobertura: null }))
      .catch(() => ({ disponible: false, cobertura: null }));
  }
  return coberturaCache;
}

export async function buscarNacional(parametros: Record<string, string | number | undefined>, f: typeof fetch = fetch): Promise<FilaNacional[]> {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(parametros)) if (v !== undefined && v !== '') q.set(k, String(v));
  const resp = await f(`${URL_API}/nacional/buscar?${q.toString()}`);
  if (resp.status === 503) throw new ErrorApi('La búsqueda nacional no está configurada en el servidor.', 503);
  const datos = await leer<{ total: number; filas: FilaNacional[] }>(resp);
  return datos.filas;
}

// ---------- Modelo LiDAR recortado (contrato_modelo) ----------

/**
 * 'techos' = techo corregido: cada faceta como superficie plana de 4 esquinas (salida Planos de Roof_from_LIDAR); falta en los modelos anteriores al 2026-09-26.
 * 'tejados' = tejado por tipo: cada cuerpo del Catastro con su superficie parametrica (plana, 1, 2 o 4 aguas, curvas, revolucion); falta en los modelos de version < 4.
 */
export type IdCapaModelo = 'terreno' | 'cubiertas' | 'techos' | 'tejados' | 'muros' | 'arboles' | 'arbustos' | 'hierba';

/** [distancia en m, cantidad con d <= distancia]; creciente, de 1 en 1 m de 0 a margenM. */
export type Cortes = [number, number][];

export interface CapaModelo {
  id: IdCapaModelo;
  nombre: string;
  /** Color por defecto de la capa, 0..1. */
  color: [number, number, number];
  elementos: number;
  triangulos: number;
  /** Cantidad de INDICES (3 por triangulo) a dibujar para cada distancia. */
  cortes: Cortes;
}

export interface ModeloInfo {
  epsg: number;
  /** UTM x, y, z: modelo.glb y nube.bin son relativos a este punto. */
  origen: [number, number, number];
  margenM: number;
  /** GeoJSON del poligono de recorte (UTM). */
  poligono: string;
  capas: CapaModelo[];
  nube: { puntos: number; cortes: Cortes };
  curvas: { intervalo: number; fuente: 'MDT50' | 'LiDAR' | null };
  terreno: { fuente: 'MDT50' | 'LiDAR' | null; paso: number };
  info: string[];
  avisos: string[];
}

/** 'tejados': contorno de cada cuerpo con tipo (p.tipo, nombre, pendiente, alero, cumbrera) y sus cumbreras y limatesas (g 'lin', p.linea). */
export type CapaPlanta = 'curvas' | 'cubiertas' | 'tejados' | 'muros' | 'huellas' | 'arboles' | 'arbustos' | 'hierba';

/** Rasgo de planta.json: proyeccion en XY (UTM absoluto) de un elemento del modelo. */
export interface RasgoPlanta {
  capa: CapaPlanta;
  /** Distancia minima del elemento al poligono (0 = toca o esta dentro). */
  d: number;
  /** 'pol': anillos (exterior + huecos); 'lin': un solo tramo en c[0]. */
  g: 'pol' | 'lin';
  c: number[][][];
  p?: Record<string, number | string | boolean>;
}

export interface Planta {
  epsg: number;
  origen: [number, number, number];
  features: RasgoPlanta[];
}

/** Nube de nube.bin: posiciones relativas al origen (Z arriba), color y clase ASPRS por punto. */
export interface NubeModelo {
  puntos: number;
  posiciones: Float32Array;
  colores: Uint8Array;
  clases: Uint8Array;
}

async function leerJson<T>(url: string, f: typeof fetch): Promise<T> {
  const resp = await f(url);
  if (!resp.ok) throw new ErrorApi(`No se pudo leer ${url.split('/').pop()}: HTTP ${resp.status}`, resp.status);
  return (await resp.json()) as T;
}

export function cargarModeloInfo(url: string, f: typeof fetch = fetch): Promise<ModeloInfo> {
  return leerJson<ModeloInfo>(url, f);
}

export async function cargarPlanta(url: string, f: typeof fetch = fetch): Promise<Planta> {
  return normalizarPlanta(await leerJson<Planta>(url, f));
}

export async function cargarNube(url: string, f: typeof fetch = fetch): Promise<NubeModelo> {
  const resp = await f(url);
  if (!resp.ok) throw new ErrorApi(`No se pudo leer la nube: HTTP ${resp.status}`, resp.status);
  return leerNube(await resp.arrayBuffer());
}

/** nube.bin: little-endian, sin cabecera, 16 bytes por punto (float32 x, y, z + uint8 r, g, b, clase). */
export function leerNube(buffer: ArrayBuffer): NubeModelo {
  const n = Math.floor(buffer.byteLength / 16);
  const dv = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const posiciones = new Float32Array(n * 3);
  const colores = new Uint8Array(n * 3);
  const clases = new Uint8Array(n);
  for (let i = 0, o = 0; i < n; i++, o += 16) {
    posiciones[i * 3] = dv.getFloat32(o, true);
    posiciones[i * 3 + 1] = dv.getFloat32(o + 4, true);
    posiciones[i * 3 + 2] = dv.getFloat32(o + 8, true);
    colores[i * 3] = bytes[o + 12];
    colores[i * 3 + 1] = bytes[o + 13];
    colores[i * 3 + 2] = bytes[o + 14];
    clases[i] = bytes[o + 15];
  }
  return { puntos: n, posiciones, colores, clases };
}

/**
 * El contrato dice "UTM absoluto, cm" para las coordenadas de planta.json. Se aceptan metros (con
 * precision de cm) o centimetros enteros: si la X del primer vertice no cabe en un huso UTM en
 * metros (> 2.000.000), se asume que viene en centimetros y se divide todo entre 100.
 */
export function normalizarPlanta(p: Planta): Planta {
  const primero = p.features.find((r) => r.c.length && r.c[0].length)?.c[0][0];
  if (!primero || Math.abs(primero[0]) < 2e6) return p;
  const features = p.features.map((r) => ({ ...r, c: r.c.map((an) => an.map((v) => [v[0] / 100, v[1] / 100])) }));
  return { ...p, features };
}

/** Cuantos elementos dibujar para un desfase: el ultimo corte con distancia <= desfase (0 si ninguno). */
export function cuentaParaDesfase(cortes: Cortes, desfase: number): number {
  let n = 0;
  for (const [d, c] of cortes) {
    if (d <= desfase + 1e-9) n = c; else break;
  }
  return n;
}

/** Clave local del modelo de una parcela o de un rectangulo de zona (cajas redondeadas al metro). */
export function claveModelo(objetivo: { rc: string } | { bbox: Bbox; epsg: number }): string {
  if ('rc' in objetivo) return `cernicalo:modelo:${objetivo.rc}`;
  const b = objetivo.bbox.map((v) => Math.round(v)).join('_');
  return `cernicalo:modelo:zona:${objetivo.epsg}_${b}`;
}

export function modeloGuardado(clave: string): string | null {
  try { return localStorage.getItem(clave); } catch { return null; }
}

export function guardarModelo(clave: string, id: string): void {
  try { localStorage.setItem(clave, id); } catch { /* sin almacenamiento */ }
}

/** Ultimo desfase usado en la escena para un modelo (para el PDF y el DXF). */
export function desfaseGuardado(clave: string): number | null {
  try {
    const v = localStorage.getItem(`${clave}:desfase`);
    return v === null ? null : Number(v);
  } catch { return null; }
}

export function guardarDesfase(clave: string, desfase: number): void {
  try { localStorage.setItem(`${clave}:desfase`, String(desfase)); } catch { /* sin almacenamiento */ }
}

/** Recuerda el area preparada para una referencia (se guarda en localStorage, por navegador). */
export function areaGuardada(rc: string): string | null {
  try { return localStorage.getItem(`cernicalo:lidar:${rc}`); } catch { return null; }
}

export function guardarArea(rc: string, id: string): void {
  try { localStorage.setItem(`cernicalo:lidar:${rc}`, id); } catch { /* sin almacenamiento */ }
}
